import { format, subMonths } from "date-fns";
import React, { useEffect, useState } from "react";
import { Cell, Pie, PieChart, Tooltip } from "recharts";
import { USDollar } from "~/functions";
import api from "~/services/api";
import useReportsStore from "~/store/reportsStore";
import { useSelector } from "react-redux";

export default function ChartExpenses({
  onCompareToggle,
  dreConfigId = null,
  dateType = null,
}) {
  const filial = useSelector((state) => state.auth.filial);
  const { filters } = useReportsStore();
  const period = filters.periodsByReport?.Expenses || filters.period;
  //   { name: "ACCOUNT TRANSFERS", value: 30000 },
  //   { name: "CAPEX", value: 383 },
  //   { name: "FINANCIAL", value: 10444 },
  //   { name: "INTERCOMPANY", value: 13780 },
  //   { name: "MILA HOLDING", value: 131121 },
  //   { name: "OPERATIONAL EXPENSES", value: 68746 },
  //   { name: "PAYROLL", value: 226141 },
  //   { name: "REFUNDS", value: 4172 },
  //   { name: "REWARD", value: 6930 },
  // ].sort((a, b) => b.value - a.value);
  const [data, setData] = useState([]);
  const COLORS = [
    "rgb(238, 88, 39)",
    "rgb(238, 39, 88)",
    "rgb(88, 39, 238)",
    "rgb(39, 88, 238)",
    "rgb(255, 193, 7)",
    "rgb(64, 224, 208)",
    "rgb(54, 69, 79)",
  ];

  function updateData() {
    const from_date = format(period.from, "yyyy-MM-dd");
    const until_date = format(period.to, "yyyy-MM-dd");
    const useCompare =
      period?.label === "Month vs Month" || period?.label === "Year vs Year";
    const from_date_before = useCompare
      ? format(period.compare_from, "yyyy-MM-dd")
      : null;
    const until_date_before = useCompare
      ? format(period.compare_to, "yyyy-MM-dd")
      : null;
    const compareQuery =
      useCompare && from_date_before && until_date_before
        ? `&from_date_before=${from_date_before}&until_date_before=${until_date_before}`
        : "";
    const dreQuery = dreConfigId ? `&dre_id=${dreConfigId}` : "";
    const dateTypeQuery = dateType ? `&date_type=${dateType}` : "";

    api
      .get(
        `/receivables-dashboard/expenses-by-chart-of-account?from_date=${from_date}&until_date=${until_date}${compareQuery}${dreQuery}${dateTypeQuery}`
      )
      .then((response) => {
        setData(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  useEffect(() => {
    updateData();
  }, [period, dreConfigId, dateType]);

  useEffect(() => {
    updateData();
  }, [filial, dreConfigId, dateType]);

  const CustomCategoryTooltip = ({ active, payload }) => {
    const [first, setFirst] = useState(true);
    useEffect(() => {
      if (active) {
        const t = setTimeout(() => setFirst(false), 50);
        return () => clearTimeout(t);
      }
      setFirst(true);
    }, [active]);

    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    if (d?.value == null) return null;

    return (
      <div
        className={`transition-all duration-200 ease-out ${
          first ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
        }`}
      >
        <div className="relative">
          <div className="rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-3">
            <p className="mb-1 font-semibold">{d.name}</p>
            <p className="text-sm">
              <span className="font-medium">
                {USDollar.format(d.value?.toFixed(0))}
              </span>{" "}
            </p>
          </div>
          <div className="pointer-events-none absolute left-1/2 -bottom-[6px] -translate-x-1/2">
            <div className="relative">
              <div className="w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-gray-200" />
              <div className="absolute left-1/2 -top-[7px] -translate-x-1/2 w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-t-[7px] border-t-white" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full p-2 px-4 border rounded-lg mb-4">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center justify-between w-full">
          <div className="flex flex-col items-start justify-start mb-2">
            <h1 className="text-lg font-bold">Expenses</h1>
            <div className="text-sm font-extralight text-gray-500">
              Total of{" "}
              {USDollar.format(
                data?.reduce((a, b) => a + b.total, 0)?.toFixed(0)
              )}
            </div>
          </div>
        </div>
      </div>
      {data.length > 0 && (
        <div
          className={`flex flex-row items-center justify-start ${
            onCompareToggle ? "cursor-pointer" : ""
          }`}
          onClick={(event) => {
            if (!onCompareToggle) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            onCompareToggle(data);
          }}
        >
          <PieChart width={200} height={142}>
            <Tooltip
              content={<CustomCategoryTooltip />}
              position={{ y: -60 }}
              offset={-50}
              cursor={{ fill: "transparent" }}
            />
            <Pie
              dataKey="total"
              isAnimationActive={false}
              data={data}
              cx="50%"
              cy="50%"
              outerRadius={60}
              innerRadius={40}
            >
              {data?.map((entry, index) => (
                <Cell
                  key={`cell-${entry.name}`}
                  fill={COLORS[index % COLORS.length]}
                  values={entry.total}
                />
              ))}
            </Pie>
          </PieChart>
          <div className="w-100 h-full flex flex-col align-center justify-start">
            {[...(data || [])]
              .sort((a, b) => (b?.total || 0) - (a?.total || 0))
              .slice(0, 5)
              .map((entry, index) => {
                const color = COLORS[index % COLORS.length];
                return (
                  <div
                    key={`expense-top-${entry?.code || entry?.name || index}`}
                    className="flex flex-row items-center justify-start gap-1"
                  >
                    <div
                      className="w-2 h-2 bg-zinc-50 rounded-sm"
                      style={{ backgroundColor: color }}
                    ></div>
                    <div className="text-[11px] text-primary">
                      <strong>{entry?.name}</strong> -{" "}
                      {USDollar.format(entry?.total?.toFixed(0))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
